import { useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "@tanstack/react-router";
import { IconButton } from "@/components/ui/Button";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Popover } from "@/components/ui/Popover";
import { RenameWorkspaceDialog } from "@/components/dialogs/RenameWorkspaceDialog";
import { WorkspaceColorDialog } from "@/components/dialogs/WorkspaceColorDialog";
import { WorkspaceIconDialog } from "@/components/dialogs/WorkspaceIconDialog";
import { WorkspaceCompanyDialog } from "@/components/dialogs/WorkspaceCompanyDialog";
import { TeamsWebhookDialog } from "@/components/dialogs/TeamsWebhookDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { useDeleteWorkspace } from "@/hooks/useDeleteWorkspace";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface Props {
  workspaceId: UUID;
  workspaceName: string;
  workspaceColor: string | null;
  workspaceIcon: string | null;
  workspaceCompany: string | null;
  teamsConfigured: boolean;
  boardCount: number;
}

export function WorkspaceMenu({
  workspaceId,
  workspaceName,
  workspaceColor,
  workspaceIcon,
  workspaceCompany,
  teamsConfigured,
  boardCount,
}: Props) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [iconOpen, setIconOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const deleteMutation = useDeleteWorkspace();

  async function onConfirmDelete() {
    try {
      await deleteMutation.mutateAsync({ workspaceId });
      setDeleteOpen(false);
      showToast(t.workspaceDeletedToast, "success");
      void navigate({ to: "/" });
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    }
  }

  return (
    <>
      <IconButton
        ref={triggerRef}
        icon="dotsV"
        size={28}
        active={open}
        title={t.more}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      />
      {open ? (
        <Popover
          anchor={triggerRef.current}
          onClose={() => setOpen(false)}
          width={220}
          align="right"
        >
          <MenuItem
            icon="edit"
            label={t.renameWorkspace}
            onClick={() => {
              setOpen(false);
              setRenameOpen(true);
            }}
          />
          <MenuItem
            icon="status"
            label={t.workspaceColorMenuItem}
            onClick={() => {
              setOpen(false);
              setColorOpen(true);
            }}
          />
          <MenuItem
            icon="box"
            label={t.workspaceIconMenuItem}
            onClick={() => {
              setOpen(false);
              setIconOpen(true);
            }}
          />
          <MenuItem
            icon="briefcase"
            label={t.workspaceCompanyMenuItem}
            onClick={() => {
              setOpen(false);
              setCompanyOpen(true);
            }}
          />
          <MenuItem
            icon="headset"
            label={teamsConfigured ? t.teamsWebhookMenuItemActive : t.teamsWebhookMenuItem}
            onClick={() => {
              setOpen(false);
              setTeamsOpen(true);
            }}
          />
          <Divider />
          <MenuItem
            icon="trash"
            label={t.deleteWorkspace}
            tone="danger"
            onClick={() => {
              setOpen(false);
              setDeleteOpen(true);
            }}
          />
        </Popover>
      ) : null}

      <RenameWorkspaceDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        workspaceId={workspaceId}
        initialName={workspaceName}
      />

      <WorkspaceColorDialog
        open={colorOpen}
        onOpenChange={setColorOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        currentColor={workspaceColor}
      />

      <WorkspaceIconDialog
        open={iconOpen}
        onOpenChange={setIconOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        currentIcon={workspaceIcon}
      />

      <WorkspaceCompanyDialog
        open={companyOpen}
        onOpenChange={setCompanyOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        currentCompany={workspaceCompany}
      />

      <TeamsWebhookDialog
        open={teamsOpen}
        onOpenChange={setTeamsOpen}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        configured={teamsConfigured}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t.deleteWorkspaceTitle}
        message={t.deleteWorkspaceMessage
          .replace("{name}", workspaceName)
          .replace("{count}", String(boardCount))}
        confirmLabel={t.deleteWorkspace}
        tone="danger"
        busy={deleteMutation.isPending}
        onConfirm={onConfirmDelete}
      />
    </>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "var(--hairline)", margin: "4px 0" }} />;
}

function MenuItem({
  icon,
  label,
  onClick,
  tone = "default",
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  tone?: "default" | "danger";
}) {
  const itemStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    color: tone === "danger" ? "var(--danger)" : "var(--ink-1)",
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
    textAlign: "left",
  };
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      style={itemStyle}
    >
      <Icon name={icon} size={14} color={tone === "danger" ? "var(--danger)" : "var(--ink-3)"} />
      {label}
    </button>
  );
}
